import { tokens, shell } from "views/Product/productPageStyle.js";

/**
 * Styles for the public brand page (/brand/:authId).
 *
 * Same journey as the record page and the failure page — a reader moves from
 * a brand to its certified products and back — so it is built from the same
 * tokens and shell: plaster ground, one white sheet, hairlines, Newsreader
 * over Inter over mono labels. No leaf, globe or tick imagery (B-01 §26.2),
 * and no score anywhere (§7).
 */
const {
  SHEET,
  INK,
  MUTED,
  RULE,
  GREEN,
  GREEN_HOVER,
  DISPLAY,
  BODY,
  MONO,
  DESKTOP,
} = tokens;

const label = {
  fontFamily: MONO,
  fontSize: "10px",
  fontWeight: 500,
  letterSpacing: "0.14em",
  lineHeight: "14px",
  color: MUTED,
  margin: 0,
};

const brandPageStyle = {
  ...shell,

  /* ------------------------------------------------------------- masthead */
  masthead: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    padding: "12px 0 30px",
    [DESKTOP]: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: "56px",
      padding: "40px 0 56px",
    },
  },
  logoBox: {
    width: "120px",
    height: "120px",
    flexShrink: 0,
    background: SHEET,
    border: `1px solid ${RULE}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px",
    boxSizing: "border-box",
    [DESKTOP]: { width: "220px", height: "220px", padding: "24px" },
  },
  logoImg: { width: "100%", height: "100%", objectFit: "contain" },
  mastheadText: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    minWidth: 0,
    flex: "1 1 auto",
    [DESKTOP]: { gap: "20px" },
  },
  eyebrow: { display: "flex", alignItems: "center", gap: "8px" },
  eyebrowRule: {
    width: "18px",
    height: "1px",
    background: GREEN,
    flexShrink: 0,
    [DESKTOP]: { width: "26px" },
  },
  eyebrowText: { ...label, color: GREEN, [DESKTOP]: { fontSize: "11px" } },
  brandName: {
    fontFamily: DISPLAY,
    fontSize: "34px",
    fontWeight: 500,
    letterSpacing: "-0.02em",
    lineHeight: "38px",
    color: INK,
    margin: 0,
    overflowWrap: "anywhere",
    [DESKTOP]: { fontSize: "60px", lineHeight: "62px", letterSpacing: "-0.025em" },
  },
  description: {
    fontFamily: BODY,
    fontSize: "16px",
    lineHeight: "26px",
    color: INK,
    margin: 0,
    maxWidth: "720px",
    whiteSpace: "pre-line",
  },
  facts: {
    display: "flex",
    flexWrap: "wrap",
    gap: "18px 48px",
    borderTop: `1px solid ${RULE}`,
    paddingTop: "18px",
  },
  fact: { display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 },
  factLabel: { ...label },
  factValue: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
    color: INK,
    margin: 0,
    overflowWrap: "anywhere",
  },
  factValueMuted: {
    fontFamily: MONO,
    fontSize: "13px",
    lineHeight: "18px",
    color: MUTED,
    margin: 0,
  },
  factLink: {
    color: GREEN,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    "&:hover": { color: GREEN_HOVER },
  },

  /* ------------------------------------------------------------- products */
  sheet: { background: SHEET, flex: "1 1 auto" },
  productsHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "16px",
    borderBottom: `1px solid ${RULE}`,
    padding: "32px 0 14px",
    [DESKTOP]: { padding: "48px 0 18px" },
  },
  productsTitle: {
    fontFamily: DISPLAY,
    fontSize: "25px",
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: "30px",
    color: INK,
    margin: 0,
    [DESKTOP]: { fontSize: "32px", lineHeight: "36px" },
  },
  productsCount: { ...label, flexShrink: 0 },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "0",
    padding: "8px 0 48px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "0 32px",
    },
    [DESKTOP]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: "0 40px",
      padding: "8px 0 72px",
    },
  },
  card: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    padding: "18px 0",
    borderBottom: `1px solid ${RULE}`,
    color: INK,
    textDecoration: "none",
    minWidth: 0,
    "&:hover $cardName": { color: GREEN },
  },
  cardPhoto: {
    width: "88px",
    height: "88px",
    flexShrink: 0,
    border: `1px solid ${RULE}`,
    padding: "6px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardPhotoImg: { width: "100%", height: "100%", objectFit: "contain" },
  cardText: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minWidth: 0,
  },
  cardName: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "22px",
    color: INK,
    margin: 0,
    overflowWrap: "anywhere",
  },
  cardMeta: {
    fontFamily: MONO,
    fontSize: "12px",
    lineHeight: "16px",
    color: MUTED,
    margin: 0,
    overflowWrap: "anywhere",
  },
  empty: {
    fontFamily: BODY,
    fontSize: "16px",
    lineHeight: "26px",
    color: MUTED,
    padding: "28px 0 56px",
    margin: 0,
  },
  stateAction: {
    display: "inline-block",
    marginTop: "18px",
    color: GREEN,
    fontWeight: 500,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
};

export default brandPageStyle;
